/**
 * Panel.jsx — shared/Panel.jsx
 *
 * A draggable, resizable floating panel used throughout the dashboard canvas.
 * Drag via the header bar. Resize via the bottom-right corner grip.
 * Uses useDraggablePanel hook which attaches listeners to window (not the element)
 * so the interaction doesn't break when the cursor moves fast.
 */

import { useDraggablePanel } from '../../hooks/useDraggablePanel';

export default function Panel({ title, icon, initialPos, initialSize, children, onClose, onChangeEnd }) {
  const { panelRef, headerRef, resizeRef, pos, size, isDragging, isResizing } =
    useDraggablePanel(initialPos, initialSize, onChangeEnd);

  return (
    <div
      ref={panelRef}
      className={`panel${isDragging ? ' dragging' : ''}${isResizing ? ' resizing' : ''}`}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h, zIndex: 10 }}
    >
      {/* Drag handle — pointer events attached inside the hook */}
      <div ref={headerRef} className="panel-header">
        <div className="panel-title">
          {icon && <span className="icon">{icon}</span>}
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Live size readout — useful when manually sizing panels */}
          <span className="faint mono" style={{ fontSize: 9 }}>
            {Math.round(size.w)}×{Math.round(size.h)}
          </span>
          {/* Close button — only shown if onClose is provided */}
          {onClose && (
            <button
              onClick={onClose}
              title="Close panel"
              style={{
                background: 'none', border: 'none', color: 'var(--faint)',
                cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 2px',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--down)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--faint)'}
            >×</button>
          )}
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="panel-body">{children}</div>

      {/* Resize grip — bottom-right corner */}
      <div ref={resizeRef} className="resize-handle" title="Drag to resize" />
    </div>
  );
}
